import React from 'react';
import { Link, useParams } from 'react-router-dom';

const APP_URL = (process.env.REACT_APP_APP_URL || 'https://app.glowapp.me').replace(/\/$/, '');
const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const NATIVE_SCHEME = 'GlowApp://';
const IOS_STORE = 'https://apps.apple.com/app/glowapp';
const ANDROID_STORE = 'https://play.google.com/store/apps/details?id=com.foo.meet';

function appBusinessUrl(businessId) {
  return `${APP_URL}/business/${businessId}`;
}

function appEmployeeUrl(businessId, employeeId) {
  return `${APP_URL}/business/${businessId}/employee/${employeeId}`;
}

function nativeBusinessUrl(businessId) {
  return `${NATIVE_SCHEME}open?businessId=${encodeURIComponent(businessId)}`;
}

function nativeEmployeeUrl(businessId, employeeId) {
  return `${NATIVE_SCHEME}open?businessId=${encodeURIComponent(businessId)}&employeeId=${encodeURIComponent(employeeId)}`;
}

function isMobileBrowser() {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function parseIdsFromWebUrl(url) {
  try {
    const path = new URL(url, APP_URL).pathname.replace(/^\/+/, '');
    const parts = path.split('/').filter(Boolean);
    if (parts[0] === 'business' && parts[1] && parts[2] === 'employee' && parts[3]) {
      return { businessId: parts[1], employeeId: parts[3] };
    }
    if (parts[0] === 'business' && parts[1]) {
      return { businessId: parts[1] };
    }
  } catch (error) {
    return null;
  }
  return null;
}

export default function ShareRedirect() {
  const { code, businessId, employeeId } = useParams();
  const [error, setError] = React.useState(false);
  const [target, setTarget] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;

    const applyTarget = (ids) => {
      if (!ids?.businessId || cancelled) return;
      const next = {
        web: ids.employeeId
          ? appEmployeeUrl(ids.businessId, ids.employeeId)
          : appBusinessUrl(ids.businessId),
        native: ids.employeeId
          ? nativeEmployeeUrl(ids.businessId, ids.employeeId)
          : nativeBusinessUrl(ids.businessId),
      };
      setTarget(next);
      if (!isMobileBrowser()) {
        window.location.replace(next.web);
        return;
      }
      window.location.href = next.native;
    };

    if (businessId && employeeId) {
      applyTarget({ businessId, employeeId });
      return () => {
        cancelled = true;
      };
    }
    if (businessId) {
      applyTarget({ businessId });
      return () => {
        cancelled = true;
      };
    }

    if (!code) {
      setError(true);
      return undefined;
    }

    const resolve = async () => {
      try {
        if (API_BASE) {
          const response = await fetch(`${API_BASE}/business/share-links/${encodeURIComponent(code)}/`);
          if (response.ok) {
            const data = await response.json();
            if (data.business_id) {
              applyTarget({
                businessId: String(data.business_id),
                employeeId: data.employee_id ? String(data.employee_id) : undefined,
              });
              return;
            }
            if (data.target_url) {
              const parsed = parseIdsFromWebUrl(data.target_url);
              if (parsed) {
                applyTarget(parsed);
                return;
              }
            }
          }
        }
        if (!cancelled) setError(true);
      } catch (err) {
        if (!cancelled) setError(true);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [code, businessId, employeeId]);

  const openNativeApp = () => {
    if (target?.native) {
      window.location.href = target.native;
    }
  };

  const openWebApp = () => {
    if (target?.web) {
      window.location.href = target.web;
    }
  };

  const openStore = () => {
    window.location.href = isIOS() ? IOS_STORE : ANDROID_STORE;
  };

  if (error) {
    return (
      <main style={styles.wrap}>
        <p style={styles.text}>This share link is no longer available.</p>
        <Link to="/" style={styles.link}>
          Back to Glow
        </Link>
      </main>
    );
  }

  if (!isMobileBrowser()) {
    return (
      <main style={styles.wrap}>
        <p style={styles.text}>Opening Glow…</p>
      </main>
    );
  }

  return (
    <main style={styles.wrap}>
      <p style={styles.text}>Open this page in the Glow app</p>
      <button type="button" style={styles.primary} onClick={openNativeApp}>
        Open in Glow
      </button>
      <button type="button" style={styles.secondary} onClick={openWebApp}>
        Continue in browser
      </button>
      <button type="button" style={styles.tertiary} onClick={openStore}>
        Get the app
      </button>
    </main>
  );
}

const styles = {
  wrap: {
    minHeight: '50vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 20px',
    textAlign: 'center',
    gap: 12,
  },
  text: {
    fontSize: 18,
    color: '#333',
    marginBottom: 8,
  },
  link: {
    color: '#6974cf',
    fontWeight: 600,
  },
  primary: {
    backgroundColor: '#6974cf',
    color: '#fff',
    border: 0,
    borderRadius: 24,
    padding: '12px 28px',
    fontSize: 16,
    fontWeight: 700,
    minWidth: 220,
    cursor: 'pointer',
  },
  secondary: {
    backgroundColor: 'transparent',
    color: '#6974cf',
    border: '1px solid #6974cf',
    borderRadius: 24,
    padding: '12px 28px',
    fontSize: 16,
    fontWeight: 600,
    minWidth: 220,
    cursor: 'pointer',
  },
  tertiary: {
    backgroundColor: 'transparent',
    color: '#666',
    border: 0,
    padding: '8px 16px',
    fontSize: 14,
    cursor: 'pointer',
  },
};
